import { isAddress } from 'ethers/lib/utils.js'
import { useRouter } from 'next/router'
import { FC, memo, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Address } from 'wagmi'

import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import NotificationContainer from 'constants/NotificationContainer'
import useENS from 'hooks/useENS'
import DelegationManageModal from 'sections/futures/DelegationTrading/DelegationManageModal'
import WatcherModeModal from 'sections/futures/WatcherMode/WatcherModeModal'
import { WatchOrConnectWalletModal } from 'sections/futures/WatcherMode/WatchOrConnectWallet'
import { selectShowModal } from 'state/app/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { resetWalletAddress } from 'state/wallet/actions'
import { MobileScreenContainer } from 'styles/common'

import Banner from '../HomeLayout/Banner'

import Footer from './Footer'
import Header from './Header'
import MobileUserMenu from './Header/MobileUserMenu'

type AppLayoutProps = {
	children: React.ReactNode
}

const AppLayout: FC<AppLayoutProps> = memo(({ children }) => {
	const openModal = useAppSelector(selectShowModal)
	const dispatch = useAppDispatch()
	const router = useRouter()

	const walletParam = useMemo(() => {
		const wallet = router.query.wallet as string
		return wallet ?? ''
	}, [router.query.wallet])

	const { ensAddress } = useENS(walletParam)

	useEffect(() => {
		if (!!ensAddress || (!!walletParam && isAddress(walletParam))) {
			dispatch(
				resetWalletAddress({
					address: (ensAddress ?? walletParam) as Address,
					selectedType: 'watcher',
				})
			)
		}
	}, [ensAddress, walletParam, dispatch])

	return (
		<AppLayoutContainer>
			<DesktopOnlyView>
				<DesktopGridContainer>
					<Header />
					<main>{children}</main>
					<Footer />
				</DesktopGridContainer>
			</DesktopOnlyView>
			<MobileOrTabletView>
				<MobileScreenContainer>
					<Banner />
					{children}
					<MobileUserMenu />
				</MobileScreenContainer>
			</MobileOrTabletView>
			<NotificationContainer />
			{openModal === 'enable_watcher_mode' && <WatcherModeModal />}
			{openModal === 'watch_or_connect_wallet' && <WatchOrConnectWalletModal />}
			<DelegationManageModal />
		</AppLayoutContainer>
	)
})

const AppLayoutContainer = styled.div`
	height: 100%;

	> div {
		height: 100%;
	}
`

const DesktopGridContainer = styled.div`
	width: 100%;
	height: 100%;
	display: grid;
	grid-template: auto 1fr auto / 100%;

	> main {
		display: flex;
		min-height: 0;
		width: 100%;

		> div {
			width: 100%;
			height: 100%;
		}
	}
`

export default AppLayout
